import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import type { ApiError } from '../api';
import { STATUS_WORDS, connectEndpoints, requirementWords } from '../api-connect';
import type { ConnectDoor, StripeAccountView } from '../api-connect';
import { Badge, Button, ErrorNote, Panel, formatWhen } from '../ui';
import type { Tone } from '../ui';

/**
 * The team's Stripe account, as a status card (Stripe Connect P2, D-393;
 * plan-stripe-connect.md §3.3). Used on both doors: Admin → Settings →
 * Payments for the team, and the STGD section for STGD staff, who also see
 * the full account id and can disconnect it.
 *
 * Coming back from Stripe's onboarding lands on this page with
 * `?stripe=return` (re-read the account) or `?stripe=refresh` (the link had
 * expired: fetch a new one and go straight back).
 */

const STATUS_TONE: Record<NonNullable<StripeAccountView['account']>['status'], Tone> = {
  ONBOARDING: 'warn',
  RESTRICTED: 'warn',
  ENABLED: 'ok',
  DISABLED: 'bad',
  DISCONNECTED: 'bad',
};

const KEY_WORDS: Record<StripeAccountView['connect']['platform_key'], string> = {
  OK: '',
  MISSING: 'STGD’s Stripe platform key is not set on this deployment yet, so the account cannot be set up from here. STGD sets it.',
  UNRECOGNIZED: 'The Stripe platform key on this deployment is not a Stripe secret key. STGD needs to replace it.',
  LIVE_OUTSIDE_PRODUCTION: 'This deployment holds a live Stripe platform key but is not production; it is refused. STGD needs to replace it with a test key.',
};

function YesNo({ on, yes, no }: { on: boolean; yes: string; no: string }) {
  return <Badge tone={on ? 'ok' : 'warn'}>{on ? yes : no}</Badge>;
}

export function StripeAccountCard({ door, view, queryKey }: { door: ConnectDoor; view: StripeAccountView | undefined; queryKey: readonly unknown[] }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const handled = useRef(false);

  const onError = (cause: ApiError) => setError(cause.message);
  const refreshAll = () => void queryClient.invalidateQueries({ queryKey: [...queryKey] });

  const onboarding = useMutation({
    mutationFn: () => connectEndpoints.onboarding(door),
    onSuccess: (link) => {
      window.location.assign(link.url);
    },
    onError,
  });
  const refresh = useMutation({ mutationFn: () => connectEndpoints.refresh(door), onSuccess: () => { setError(null); refreshAll(); }, onError });
  const oauth = useMutation({ mutationFn: () => connectEndpoints.oauthStart(door), onSuccess: (link) => window.location.assign(link.url), onError });

  // Back from Stripe: `return` re-reads the account; `refresh` means the link expired, so get another at once.
  const back = params.get('stripe');
  useEffect(() => {
    if (!back || handled.current || !view?.account) return;
    handled.current = true;
    setParams((before) => {
      const next = new URLSearchParams(before);
      next.delete('stripe');
      return next;
    }, { replace: true });
    if (back === 'refresh') onboarding.mutate();
    else refresh.mutate();
  }, [back, view?.account, setParams, onboarding, refresh]);

  const account = view?.account ?? null;
  const connect = view?.connect;
  const keyProblem = connect && connect.platform_key !== 'OK' ? KEY_WORDS[connect.platform_key] : null;
  const due = account ? [...account.requirements.past_due, ...account.requirements.currently_due.filter((code) => !account.requirements.past_due.includes(code))] : [];

  return (
    <Panel
      title="Stripe account"
      description="Where online card payments go. The team owns this account, is the merchant customers see, and is paid out by Stripe directly."
      actions={
        account ? (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => refresh.mutate()} disabled={refresh.isPending}>
              {refresh.isPending ? 'Checking…' : 'Check with Stripe'}
            </Button>
            <a className="inline-flex items-center rounded border border-line bg-surface px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-page" href={account.dashboard_url} target="_blank" rel="noreferrer">
              Open Stripe Dashboard
            </a>
          </div>
        ) : null
      }
    >
      <div className="space-y-4 px-5 py-4">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {keyProblem ? <p className="rounded border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">{keyProblem}</p> : null}

        {!account ? (
          <div className="space-y-3">
            <p className="text-sm text-ink-600">No Stripe account is connected yet. Setting one up takes about ten minutes with Stripe: the business’s details, a bank account for payouts, and the person responsible.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" disabled={!connect || connect.platform_key !== 'OK' || onboarding.isPending} onClick={() => onboarding.mutate()}>
                {onboarding.isPending ? 'Opening Stripe…' : 'Set up a new Stripe account'}
              </Button>
              {connect?.oauth_available ? (
                <Button disabled={connect.platform_key !== 'OK' || oauth.isPending} onClick={() => oauth.mutate()}>
                  Connect an existing Stripe account
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-semibold text-ink-900">{account.display_name ?? 'Unnamed account'}</p>
              <Badge tone={STATUS_TONE[account.status]}>{STATUS_WORDS[account.status]}</Badge>
              <span className="font-mono text-xs text-ink-400">{account.stripe_account_id}</span>
              <Badge tone={account.livemode ? 'accent' : 'neutral'}>{account.livemode ? 'Live' : 'Test mode'}</Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              <YesNo on={account.details_submitted} yes="Details submitted" no="Details not submitted" />
              <YesNo on={account.charges_enabled} yes="Can take payments" no="Cannot take payments" />
              <YesNo on={account.payouts_enabled} yes="Payouts on" no="Payouts off" />
            </div>
            {due.length > 0 || account.requirements.disabled_reason ? (
              <div className="rounded border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-ink-900">
                <p className="font-medium">
                  Stripe needs {due.length === 1 ? 'one more thing' : `${due.length} more things`}
                  {account.requirements.current_deadline ? ` by ${formatWhen(account.requirements.current_deadline)}` : ''}:
                </p>
                <ul className="mt-1 list-disc pl-5">
                  {due.map((code) => (
                    <li key={code}>
                      {requirementWords(code)}
                      {account.requirements.past_due.includes(code) ? <span className="ml-1 text-xs font-semibold text-bad">overdue</span> : null}
                    </li>
                  ))}
                </ul>
                {account.requirements.disabled_reason ? <p className="mt-1 text-xs text-ink-600">Stripe’s reason: {account.requirements.disabled_reason.replaceAll('_', ' ').replaceAll('.', ': ')}</p> : null}
              </div>
            ) : null}
            {account.requirements.pending_verification.length > 0 ? <p className="text-xs text-ink-600">Stripe is checking {account.requirements.pending_verification.length} thing{account.requirements.pending_verification.length === 1 ? '' : 's'} already sent.</p> : null}
            {account.status !== 'ENABLED' && account.connection_method === 'ACCOUNT_LINK' ? (
              <Button variant="primary" disabled={onboarding.isPending} onClick={() => onboarding.mutate()}>
                {onboarding.isPending ? 'Opening Stripe…' : 'Continue setup'}
              </Button>
            ) : null}
            {account.status !== 'ENABLED' && account.connection_method === 'OAUTH' ? <p className="text-sm text-ink-600">This account was connected from an existing Stripe account: finish anything Stripe asks for in its own Dashboard.</p> : null}
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-ink-600 sm:grid-cols-2">
              <div>
                <dt className="inline font-semibold">Connected: </dt>
                <dd className="inline">{formatWhen(account.connected_at)}{account.connected_by ? ` by ${account.connected_by}` : ''} ({account.connection_method === 'OAUTH' ? 'existing account' : 'new account'})</dd>
              </div>
              <div>
                <dt className="inline font-semibold">Last checked with Stripe: </dt>
                <dd className="inline">{formatWhen(account.last_synced_at)}</dd>
              </div>
              {account.statement_descriptor ? (
                <div>
                  <dt className="inline font-semibold">On card statements: </dt>
                  <dd className="inline">{account.statement_descriptor} (changed in the Stripe Dashboard)</dd>
                </div>
              ) : null}
            </dl>
          </>
        )}

        {view?.platform_fee ? (
          <p className="text-sm text-ink-600">
            <span className="font-semibold">STGD’s fee: </span>
            {view.platform_fee.current && view.platform_fee.current.amount_cents > 0 ? `$${(view.platform_fee.current.amount_cents / 100).toFixed(2)} per ticket sold by card, since ${view.platform_fee.current.effective_on}` : 'none at present'}
            {view.platform_fee.upcoming.map((change) => `; from ${change.effective_on}, ${change.amount_cents > 0 ? `$${(change.amount_cents / 100).toFixed(2)} per ticket` : 'none'}`).join('')}
            . Taken by Stripe from the payout, never shown to customers; set by STGD.{view.platform_fee.collecting ? '' : ' Not collected yet.'}
          </p>
        ) : null}
        <p className="text-xs text-ink-400">
          {view?.online_payments_open ? 'Customers can pay by card online right now.' : 'Customers cannot pay by card online right now; the site tells them only that it can’t be bought online at the moment.'}
          {connect?.connect_mode === 'OFF' ? ' Card payments still go to the previous Stripe account until STGD switches this deployment over.' : ''}
        </p>
      </div>
    </Panel>
  );
}

/** Where Stripe sends the browser back after connecting an existing account (OAuth). */
export function StripeConnectReturn({ door }: { door: ConnectDoor }) {
  const [params] = useSearchParams();
  const code = params.get('code');
  const state = params.get('state');
  const denied = params.get('error_description') ?? params.get('error');
  const complete = useQuery({
    queryKey: ['stripe-connect-return', code, state],
    queryFn: () => connectEndpoints.oauthComplete(door, code!, state!),
    enabled: Boolean(code && state),
    retry: false,
    staleTime: Infinity,
  });
  if (denied) return <ErrorNote>Stripe did not connect the account: {denied}</ErrorNote>;
  if (!code || !state) return <ErrorNote>This page is where Stripe returns after connecting an account; open it from Settings → Payments.</ErrorNote>;
  if (complete.isPending) return <p className="text-sm text-ink-600">Connecting the Stripe account…</p>;
  if (complete.error) return <ErrorNote>{(complete.error as ApiError).message}</ErrorNote>;
  return <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">Connected {complete.data?.connected}. It now shows under Settings → Payments.</p>;
}
